/**
 * Options step 1 — send defaults in three clusters: Recipients, Delivery
 * (TLD, shortlink format, batching) and AI. The AI↔batches exclusion is
 * enforced by the reducer; this component only reports it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot, Switch } from '@/components/ui';

import type { ContainerEditor } from '../state/useContainerEditor';
import { DeliveryDefaults } from './DeliveryDefaults';

const GROUP_TITLE =
  'text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
}

export const SendDefaultsStep: FC<Props> = ({ editor, countryKey }) => {
  const { draft, setSendDefaults } = editor;
  const d = draft.sendDefaults;

  return (
    <div className="flex flex-wrap gap-x-10 gap-y-4">
      <div className="flex flex-col gap-3">
        <span className={GROUP_TITLE}>Recipients</span>
        <div className="flex items-center gap-2">
          <Switch
            checked={d.forceSend}
            onCheckedChange={(v) => setSendDefaults({ forceSend: v })}
            aria-label="Duplicate"
          />
          <span className="text-sm">Duplicate</span>
          <HintDot label="The sheet's DUPLICATE column — on: recipients who already received an SMS today get this one too; off: at most one SMS per number per day." />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={d.useOnlyNewDomains}
            onCheckedChange={(v) => setSendDefaults({ useOnlyNewDomains: v })}
            aria-label="Only new domains"
          />
          <span className="text-sm">Only new domains</span>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:border-l sm:border-border/50 sm:pl-10">
        <span className={GROUP_TITLE}>Delivery</span>
        <DeliveryDefaults editor={editor} countryKey={countryKey} />
      </div>
      <div className="flex flex-col gap-3 sm:border-l sm:border-border/50 sm:pl-10">
        <span className={GROUP_TITLE}>AI</span>
        <div className="flex items-center gap-2">
          <Switch
            checked={d.aiOptimizationEnabled}
            onCheckedChange={(v) =>
              setSendDefaults({ aiOptimizationEnabled: v })
            }
            aria-label="AI optimization"
          />
          <span className="text-sm">AI optimization</span>
          <HintDot label="SMSWiser's own send-time optimization. Enabling it clears batches (and vice versa) — they cannot combine." />
        </div>
        {d.aiOptimizationEnabled && (d.batchAmount || d.batchDelay) ? null : (
          <p className="max-w-[180px] text-[11px] text-muted-foreground">
            {d.aiOptimizationEnabled
              ? 'On — batches disabled.'
              : 'Off — batches available.'}
          </p>
        )}
      </div>
    </div>
  );
};
